<template>
    <div>
        <h3 class="mt-3">{{ index + 1 }} - {{ question.translation.name }}</h3>
        <template v-if="question.answer || question.upload">
            <Badge v-if="exerciseWaiting" class="mb-2" type="info">
                <i style="color: #FFF" class="mdi mdi-clock-outline"></i>
                <span style="color: #FFF">{{$t('lesson.exercise_waiting')}}</span>
            </Badge>
            <Badge v-else-if="!question.approve" class="mb-2" type="success">
                <i :style="{color: '#FFF'}" class="mdi mdi-check-all"></i>
                <span :style="{color: '#FFF'}">{{$t('lesson.exercise_revised')}}</span>
            </Badge>
            <template v-else class="d-flex flex-row">
                <Badge v-if="question.answer.approve_question" class="mb-2" type="success">
                    <i :style="{color: '#FFF'}" class="mdi mdi-check-all"></i>
                    <span :style="{color: '#FFF'}">{{$t('lesson.approved')}}</span>
                </Badge>
                <Badge v-else class="mb-2" type="danger">
                    <i :style="{color: '#FFF'}" class="mdi mdi-check-all"></i>
                    <span :style="{color: '#FFF'}">{{$t('lesson.reproved')}}</span>
                </Badge>
            </template>
            <div v-if="question.answer && question.answer.justify_obs">
                <h4>{{$t('lesson.obs')}}</h4>
                <p class="text-muted">
                    <i v-html="question.answer.justify_obs"></i>
                </p>
                <div class="d-flex justify-content-end">
                    <h4>
                        {{$t('lesson.revised_by')}}
                        <span class="text-muted">{{question.answer.justified_by}}</span>
                    </h4>
                </div>
            </div>
            <div v-if="this.question.answer.correction_date">
                <h4 class="d-flex justify-content-end">
                    {{$t('lesson.corrected_on')}}:
                    <span class="text-muted ml-1">{{ formatDate(this.question.answer.correction_date) }}</span>
                </h4>
            </div>
        </template>
        <span
            v-if="question.translation.description"
            class="mb-3 d-block flex-c"
            v-html="question.translation.description"
        ></span>
        <p
            style="font-style: italic"
            v-if="question.translation.observation"
            class="mb-3 d-block"
        >{{ question.translation.observation }}</p>
        <Editor
            v-if="!isFinished"
            class="nch--html-editor"
            v-model="answer"
            :name="$t('lesson.answer_placeholder')"
        ></Editor>
        <p v-else class="answer" v-html="answer"></p>
        <div class="d-flex">
            <template v-if="isUploadValid">
                <a
                    target="_blank"
                    :href="file(upload[0])"
                    :style="$bgColor(color.primary)"
                    class="btn d-flex"
                    id="download_button">
                    <i class="fas fa-file-alt" id="download_button"></i>
                    <p class="text">{{ $t('lesson.answer_download') }}</p>
                </a>
                <BaseButton outline type="danger" @click="removeFile">
                    <span class="mdi mdi-delete">{{ $t('common.remove') }}</span>
                </BaseButton>
            </template>
            <template v-else-if="!isFinished && !hasExistingUpload">
                <BaseButton outline type="secondary" @click="modal=true" :class="{ verifyValidate: verifyValidate && error.has('upload') }">
                    <span class="mdi mdi-paperclip">{{ $t('common.attach') }}</span>
                </BaseButton>
            </template>
            <UploadDropzoneModal
                :modal="modal"
                @close="modal=false"
                @success="filesChange"
                v-model="upload"
                ref="dropzone"
                data-vv-name="upload"
                v-validate="'required'"
            ></UploadDropzoneModal>
        </div>
        <span v-if="error.has('upload') && verifyValidate" class="invalid-feedback" style="display: block;">
            {{ error.first("upload") }}
        </span>
    </div>
</template>

<script>
import BaseButton from '../../argon/components/BaseButton';
import Badge from '../../argon/components/Badge';
import debounce from '../../helpers/helpers';
import UploadDropzoneModal from '../Common/Upload/UploadDropzoneModal';
import Editor from '../../argon/components/Inputs/HtmlEditor';
import formatDate from '../../mixins/FormatDate';

export default {
    name: 'ExerciseUpload',
    mixins: [formatDate],
    components: {
        BaseButton,
        Badge,
        UploadDropzoneModal,
        Editor,
    },
    data() {
        return {
            modal: false,
            upload: [],
            answer: '',
            uploadLoading: false,
            deleteLoading: false,
            uploadSubmited: false,
            fileChanged: false,
            error: this.$validator.errors,
            hasExistingUpload: false, // New state to check if there's an existing upload
        };
    },
    props: {
        index: {
            type: Number,
            required: true,
        },
        question: {
            type: Object,
            required: true,
        },
        isFinished: {
            type: Boolean,
        },
        verifyValidate: {
            type: Boolean,
        },
    },
    watch: {
        answer: debounce(function () {
            if (!this.isFinished) {
                this.submit();
            }
        }, 500),

        question(newVal) {
            if (newVal.answer) {
                this.answer = newVal.answer.text;
                this.upload = [];
                this.uploadSubmited = false;
            } else {
                this.upload = [];
                this.uploadSubmited = false;
            }
        },
    },
    computed: {
        color() {
            return this.$store.getters.COLOR;
        },
        exercise() {
            return this.$store.getters.LOADED_EXERCISE;
        },
        user() {
            return this.$store.getters.USER;
        },
        exerciseWaiting() {
            return this.question.answer == this.answer || (this.question.answer && this.question.answer.grade === null);
        },
        isUploadValid() {
            if (this.upload.length > 0) {
                const file = this.upload[0];
                return file.hash && file.original_file;
            }
            return false;
        },
    },
    methods: {
        filesChange(upload) {
            if (!upload.host || !upload.hash || !upload.name || !upload.type || !upload.size || !upload.mime) {
                this.$notify({
                    type: 'danger',
                    message: this.$t('content.exercise.error.upload'),
                });
                return false;
            }
            this.upload = [{
                host: upload.host,
                filename: upload.name,
                hash: upload.hash,
                type: upload.type,
                size: upload.size,
                mime: upload.mime,
                original_file: upload.original_file,
                status: 1,
                author_id: this.user.id,
                width: upload.width,
                height: upload.height,
                path: upload.path,
                // Não é coluna real (some no submit ao backend), mas sem isso o
                // preview local perde as urls assinadas do S3 e cai no fallback
                // /storage/ antigo, quebrando a visualização/redirect do arquivo.
                urls: upload.urls,
            }];
            this.fileChanged = true;
            this.submit();
            return true;
        },

        async submit() {
            this.uploadLoading = true;
            if (this.fileChanged) {
                this.uploadSubmited = true;
                // urls não é coluna real (é só pro preview local) — mandar pro
                // backend quebra o update() do model Upload ("Column not found: urls").
                const { urls, ...uploadPayload } = this.upload[0];
                await this.$store.dispatch('SUBMIT_OPTION', {
                    exerciseId: this.exercise.id,
                    questionId: this.question.id,
                    answer: {
                        text: this.answer,
                        upload: uploadPayload,
                    },
                });
            } else {
                const response = await this.$store.dispatch('SUBMIT_OPTION', {
                    exerciseId: this.exercise.id,
                    questionId: this.question.id,
                    answer: {
                        text: this.answer,
                    },
                });

                if (!response) {
                    this.answer = '';
                    this.upload = [];
                    this.$notify({
                        type: 'danger',
                        message: this.$t('content.exercise.error.no_connection'),
                    });
                }
            }
            this.uploadLoading = false;
            this.fileChanged = false;
        },

        async removeFile() {
            if (this.upload.length && this.upload[0].hash) {
                try {
                    this.deleteLoading = true;
                    await window.axios.delete(`/api/upload-local/delete/${this.upload[0].hash}`, {

                    });
                    this.upload = [];
                    this.hasExistingUpload = false; // Update the state to allow new uploads
                    this.$notify({
                        type: 'success',
                        message: 'Arquivo removido com sucesso!',
                    });
                } catch (error) {
                    this.$notify({
                        type: 'danger',
                        message: 'Erro ao remover arquivo',
                    });
                } finally {
                    this.deleteLoading = false;
                }
            }
        },

        file(upload, size = 'original') {
            if (upload.urls && upload.urls.download) {
                return upload.urls.download;
            }
            if (upload.urls && upload.urls.original) {
                return upload.urls.original;
            }
            const { host, original_file } = upload;
            return `/storage/${host || null}/original/${original_file}`;
        },

        checkExistingUpload() {
            this.hasExistingUpload = !!this.question.upload || !!this.question.answer?.upload;
            if (this.hasExistingUpload) {
                const existingUpload = this.question.upload || this.question.answer.upload;
                if (existingUpload.hash && existingUpload.original_file) {
                    this.upload = [existingUpload];
                } else {
                    this.hasExistingUpload = false;
                }
            }
        },
    },
    mounted() {
        const answer = this.question.answer ? this.question.answer.text : null;
        if (answer) {
            this.answer = answer;
        }
        this.checkExistingUpload(); // Check for existing upload on mount
    },
};
</script>

<style lang="scss" scoped>
#download_button {
    color: white;

    i {
        font-size: 1.4rem;
    }

    .text {
        margin-left: 0.6rem;
        font-family: inherit;
        font-size: 0.9rem;
        font-weight: bold;
        margin: 0;
    }
}
.flex-c {
    display: flex !important;
    flex-direction: column;
}
</style>

<style>
.answer p > img,
.justify p > img {
    width: 100%;
}
.answer {
    padding: 10px;
    max-height: 200px;
    overflow: auto;
    background-color: #f4f5f7;
}
.verifyValidate {
    border: 2px solid red;
}
</style>
